var LOGIC=LOGIC||{};LOGIC.dividingHead=(function(){/**
 * Dividing head maths for Benchsmith (plain/simple indexing).
 * 40:1 worm, standard Brown & Sharpe index plates.
 *
 * Exactly what the v2 mockup (screens/dividing-head.html) runs. Pure functions, no DOM.
 */

const WORM_RATIO = 40;

/** Brown & Sharpe plates 1–3, hole circles inner → outer. */
const BS_PLATES = [
  [15, 16, 17, 18, 19, 20],
  [21, 23, 27, 29, 31, 33],
  [37, 39, 41, 43, 47, 49],
];

/**
 * Solve N equal divisions.
 * Returns { ok, turns, holes, circle, plate } where plate is 0-based (plate 1 = 0).
 * - holes === 0 → whole turns only (circle/plate null; the UI rests the pin on plate 1, 20-hole circle).
 * - ok === false with reason 'differential' → no standard plate works (e.g. 51); needs differential indexing.
 * Picks the first circle that works, scanning plate 1 → 3, small → large (27 → 1 turn + 13 holes on the 27 circle).
 */
function solveIndexing(N, { ratio = WORM_RATIO, plates = BS_PLATES } = {}) {
  if (!Number.isInteger(N) || N < 2) return { ok: false, reason: 'invalid', turns: 0, holes: 0, circle: null, plate: null };
  const turns = Math.floor(ratio / N), rem = ratio % N;
  if (rem === 0) return { ok: true, turns, holes: 0, circle: null, plate: null };
  for (let p = 0; p < plates.length; p++) {
    for (const c of plates[p]) {
      if ((rem * c) % N === 0) return { ok: true, turns, holes: (rem * c) / N, circle: c, plate: p };
    }
  }
  return { ok: false, reason: 'differential', turns, holes: 0, circle: null, plate: null };
}

/** Crank travel for one index, in degrees: turns × 360 + holes × (360 / circle). */
function crankDegreesPerIndex(sol) {
  if (!sol.ok) return 0;
  return sol.turns * 360 + (sol.holes ? (sol.holes * 360) / sol.circle : 0);
}

/**
 * Sector arms: they span `holes` SPACES, i.e. holes + 1 holes including the one the pin sits in.
 * After every index both arms advance together by holes × (360 / circle) so the near arm meets the pin again.
 */
function sectorDegreesPerIndex(sol) {
  return sol.ok && sol.holes ? (sol.holes * 360) / sol.circle : 0;
}

/** Spindle (and gear blank) rotation = crank rotation / worm ratio. One index = 360 / N degrees. */
const spindleDegrees = (crankDeg, ratio = WORM_RATIO) => crankDeg / ratio;

/** The six animated phases of one "Index + cut". */
const PHASES = ['pull', 'turn', 'count', 'drop', 'swing', 'cut'];

/** Phase durations in ms. Teach = slow and explanatory, Quick = snappy. Long jobs are capped. */
function phaseDurations(sol, mode = 'teach') {
  const q = mode === 'quick';
  return {
    pull: q ? 140 : 450,
    turn: sol.turns ? Math.min((q ? 380 : 950) * sol.turns, q ? 1500 : 4200) : 0,
    count: sol.holes ? Math.min((q ? 34 : 150) * sol.holes, q ? 900 : 3200) : 0,
    drop: q ? 140 : 420,
    swing: sol.holes ? (q ? 280 : 750) : 0,
    cut: q ? 480 : 1200,
  };
}

const totalDuration = (D) => D.pull + D.turn + D.count + D.drop + D.swing + D.cut;

const easeInOutQuad = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

/**
 * One animation frame of indexing step `step` (0-based: about to cut tooth step + 1), `t` ms after pressing Index + cut.
 * Returns everything the plate, crank, arms and gear blank need:
 *   phase: -1 idle | 0 pull | 1 turn | 2 count | 3 drop | 4 swing | 5 cut | 6 finished
 *   crankDeg, sectorDeg  absolute angles (clockwise from 12 o'clock)
 *   pinLift 0..1         plunger pulled out (draw it ~20% larger + stronger shadow)
 *   holesCounted         how many holes past the start hole are lit/numbered
 *   turnNow              1-based turn counter shown during phase 1
 *   feed 0..1            cutter depth during the cut (in, dwell, out)
 *   ripple {p} | null    0..1 progress of the ring when the pin drops into its hole
 */
function indexFrame(sol, step, t, D) {
  const sp = sol.holes ? 360 / sol.circle : 0, frac = sol.holes * sp, stepAng = crankDegreesPerIndex(sol);
  const base = step * stepAng;
  const f = { phase: -1, crankDeg: base, sectorDeg: step * frac, pinLift: 0, holesCounted: 0, turnNow: 0, feed: 0, ripple: null };
  if (t == null) return f;
  if (t < D.pull) return { ...f, phase: 0, pinLift: easeInOutQuad(t / D.pull) };
  t -= D.pull;
  if (t < D.turn) { const e = easeInOutQuad(t / D.turn) * sol.turns; return { ...f, phase: 1, pinLift: 1, crankDeg: base + 360 * e, turnNow: Math.min(sol.turns, Math.floor(e) + 1) }; }
  t -= D.turn;
  if (t < D.count) {
    const per = D.count / sol.holes, i = Math.floor(t / per), g = (t % per) / per;
    return { ...f, phase: 2, pinLift: 1, crankDeg: base + 360 * sol.turns + (i + easeInOutQuad(Math.min(1, g * 1.4))) * sp, holesCounted: i + (g > 0.72 ? 1 : 0) };
  }
  t -= D.count;
  if (t < D.drop) { const p = t / D.drop; return { ...f, phase: 3, crankDeg: base + stepAng, holesCounted: sol.holes, pinLift: 1 - easeInOutQuad(p), ripple: { p } }; }
  t -= D.drop;
  if (t < D.swing) return { ...f, phase: 4, crankDeg: base + stepAng, holesCounted: sol.holes, sectorDeg: step * frac + frac * easeInOutQuad(t / D.swing) };
  t -= D.swing;
  if (t < D.cut) { const p = t / D.cut, feed = p < 0.45 ? easeInOutQuad(p / 0.45) : p < 0.6 ? 1 : easeInOutQuad((1 - p) / 0.4); return { ...f, phase: 5, crankDeg: base + stepAng, sectorDeg: (step + 1) * frac, feed }; }
  return { ...f, phase: 6, crankDeg: base + stepAng, sectorDeg: (step + 1) * frac };
}

/** Index of the hole the pin starts in for a given step (on the selected circle). */
function startHoleIndex(sol, step) {
  if (!sol.holes) return 0;
  const sp = 360 / sol.circle, a = (((step * crankDegreesPerIndex(sol)) % 360) + 360) % 360;
  return Math.round(a / sp) % sol.circle;
}

/** Label for the result line, e.g. "1 turn + 13 holes". */
function describe(sol) {
  if (!sol.ok) return sol.reason === 'differential' ? 'No plate fits' : 'Enter 2–400';
  const t = sol.turns === 1 ? '1 turn' : sol.turns + ' turns';
  if (!sol.holes) return t;
  if (!sol.turns) return sol.holes + ' holes';
  return t + ' + ' + sol.holes + ' holes';
}
return {WORM_RATIO,BS_PLATES,solveIndexing,crankDegreesPerIndex,sectorDegreesPerIndex,spindleDegrees,PHASES,phaseDurations,totalDuration,indexFrame,startHoleIndex,describe}})();
